import React, { useEffect, useState, createElement } from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Phone, Mail, Calendar, Send } from 'lucide-react';
import { locations, LocationData } from '../../data/locations';
import { Button } from '../ui/Button';
export function LocationContactSelector() {
  const [selectedLocation, setSelectedLocation] = useState<LocationData | null>(null);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    message: ''
  });
  // Load reCAPTCHA script
  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://www.google.com/recaptcha/api.js';
    script.async = true;
    script.defer = true;
    document.body.appendChild(script);
    return () => {
      document.body.removeChild(script);
    };
  }, []);
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Form submission placeholder
    console.log('Contact form submitted for:', selectedLocation?.name);
  };
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };
  return <section className="py-16 bg-slate-50 border-y border-slate-200" data-id="element-85">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-86">
        <div className="text-center mb-10" data-id="element-87">
          <h2 className="text-3xl font-bold text-[#1a2332] mb-4" data-id="element-88">
            Location-Specific Contact
          </h2>
          <p className="text-slate-600" data-id="element-89">
            Select a location to view contact details for that school.
          </p>
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 shadow-lg overflow-hidden" data-id="element-90">
          {/* Location Selector */}
          <div className="bg-[#1a2332] p-6 text-white" data-id="element-91">
            <h3 className="text-xl font-bold mb-4 text-center" data-id="element-92">
              Select a Location
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3" data-id="element-93">
              {locations.map(loc => <button key={loc.id} onClick={() => setSelectedLocation(loc)} className={`px-4 py-3 rounded-lg font-medium transition-colors ${selectedLocation?.id === loc.id ? 'bg-[#dc2626] text-white' : 'bg-white/10 text-white hover:bg-white/20'}`} data-id="element-94">
                  {loc.city}
                </button>)}
            </div>
          </div>

          {/* Contact Details */}
          <div className="p-8 md:p-12 min-h-[300px]" data-id="element-95">
            {selectedLocation ? <div className="max-w-2xl mx-auto animate-in fade-in duration-300" data-id="element-96">
                {/* Location Info */}
                <div className="text-center mb-8" data-id="element-97">
                  <div className="h-16 w-16 bg-slate-100 rounded-full flex items-center justify-center mb-6 mx-auto text-[#dc2626]" data-id="element-98">
                    <MapPin className="h-8 w-8" data-id="element-99" />
                  </div>
                  <h3 className="text-2xl font-bold text-[#1a2332] mb-6" data-id="element-100">
                    {selectedLocation.name}
                  </h3>

                  <div className="space-y-4 mb-6" data-id="element-101">
                    <div className="flex items-start justify-center p-4 bg-slate-50 rounded-lg" data-id="element-102">
                      <MapPin className="h-5 w-5 text-[#dc2626] mr-3 mt-0.5 flex-shrink-0" data-id="element-103" />
                      <span className="text-slate-700 font-medium text-left" data-id="element-104">
                        {selectedLocation.address}
                      </span>
                    </div>

                    <div className="flex items-center justify-center p-4 bg-slate-50 rounded-lg" data-id="element-105">
                      <Phone className="h-5 w-5 text-[#dc2626] mr-3 flex-shrink-0" data-id="element-106" />
                      <a href={`tel:${selectedLocation.phone}`} className="text-slate-700 font-medium hover:text-[#dc2626] transition-colors" data-id="element-107">
                        {selectedLocation.phone}
                      </a>
                    </div>

                    {selectedLocation.email && <div className="flex items-center justify-center p-4 bg-slate-50 rounded-lg" data-id="element-108">
                        <Mail className="h-5 w-5 text-[#dc2626] mr-3 flex-shrink-0" data-id="element-109" />
                        <a href={`mailto:${selectedLocation.email}`} className="text-slate-700 font-medium hover:text-[#dc2626] transition-colors" data-id="element-110">
                          {selectedLocation.email}
                        </a>
                      </div>}

                    <Link to={`/locations/${selectedLocation.id}#schedule-section`} className="flex items-center justify-center p-4 bg-[#dc2626] text-white rounded-lg hover:bg-[#b91c1c] transition-colors" data-id="element-111">
                      <Calendar className="h-5 w-5 mr-3 flex-shrink-0" data-id="element-112" />
                      <span className="font-medium" data-id="element-113">View Class Schedule</span>
                    </Link>
                  </div>
                </div>

                {/* Contact Form */}
                <div className="border-t border-slate-200 pt-8" data-id="element-114">
                  <h4 className="text-xl font-bold text-[#1a2332] mb-6 text-center" data-id="element-115">
                    Send a Message
                  </h4>
                  <form onSubmit={handleSubmit} className="space-y-4" data-id="element-116">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-117">
                      <div data-id="element-118">
                        <label htmlFor="firstName" className="block text-sm font-medium text-slate-700 mb-2" data-id="element-119">
                          First Name <span className="text-[#dc2626]" data-id="element-120">*</span>
                        </label>
                        <input type="text" id="firstName" name="firstName" value={formData.firstName} onChange={handleChange} required className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#dc2626] focus:border-transparent" placeholder="First name" data-id="element-121" />
                      </div>

                      <div data-id="element-122">
                        <label htmlFor="lastName" className="block text-sm font-medium text-slate-700 mb-2" data-id="element-123">
                          Last Name <span className="text-[#dc2626]" data-id="element-124">*</span>
                        </label>
                        <input type="text" id="lastName" name="lastName" value={formData.lastName} onChange={handleChange} required className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#dc2626] focus:border-transparent" placeholder="Last name" data-id="element-125" />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-126">
                      <div data-id="element-127">
                        <label htmlFor="email" className="block text-sm font-medium text-slate-700 mb-2" data-id="element-128">
                          Email <span className="text-[#dc2626]" data-id="element-129">*</span>
                        </label>
                        <input type="email" id="email" name="email" value={formData.email} onChange={handleChange} required className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#dc2626] focus:border-transparent" placeholder="your@email.com" data-id="element-130" />
                      </div>

                      <div data-id="element-131">
                        <label htmlFor="phone" className="block text-sm font-medium text-slate-700 mb-2" data-id="element-132">
                          Phone <span className="text-[#dc2626]" data-id="element-133">*</span>
                        </label>
                        <input type="tel" id="phone" name="phone" value={formData.phone} onChange={handleChange} required className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#dc2626] focus:border-transparent" placeholder="(555) 123-4567" data-id="element-134" />
                      </div>
                    </div>

                    <div data-id="element-135">
                      <label htmlFor="message" className="block text-sm font-medium text-slate-700 mb-2" data-id="element-136">
                        Message
                      </label>
                      <textarea id="message" name="message" value={formData.message} onChange={handleChange} rows={4} className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#dc2626] focus:border-transparent resize-none" placeholder="Tell us about your interest..." data-id="element-137" />
                    </div>

                    {/* reCAPTCHA */}
                    <div className="flex justify-center" data-id="element-138">
                      <div className="g-recaptcha" data-sitekey="6LeIxAcTAAAAAJcZVRqyHh71UMIEGNQ_MXjiZKhI" data-id="element-139"></div>
                    </div>

                    <Button type="submit" variant="primary" size="lg" className="w-full justify-center" data-id="element-140">
                      <Send className="mr-2 h-5 w-5" data-id="element-141" />
                      Send Message
                    </Button>
                  </form>
                </div>
              </div> : <div className="text-center flex flex-col items-center justify-center min-h-[300px]" data-id="element-142">
                <div className="h-16 w-16 bg-slate-200 rounded-full flex items-center justify-center mb-6 mx-auto text-slate-400" data-id="element-143">
                  <MapPin className="h-8 w-8" data-id="element-144" />
                </div>
                <h3 className="text-xl font-medium text-slate-700 mb-2" data-id="element-145">
                  Select a Location
                </h3>
                <p className="text-slate-500" data-id="element-146">
                  Choose a location above to view contact details for that
                  school.
                </p>
              </div>}
          </div>
        </div>
      </div>
    </section>;
}